import React, { useCallback, useMemo } from "react";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box, Chip, Stack, Typography } from "@mui/material";

function StageNode({ data, selected }) {
  return (
    <Box
      aria-label={`${data.name}, ${data.criterionLabel}`}
      sx={{
        minWidth: 210,
        p: 1.5,
        bgcolor: "background.paper",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={!data.readOnly}
        style={data.readOnly ? { background: "#00897b" } : undefined}
      />
      <Stack direction="row" spacing={0.5} mb={0.5}>
        {data.isEntry && <Chip size="small" color="primary" label="Entry" />}
        {data.isTerminal && <Chip size="small" label="Terminal" />}
      </Stack>
      <Typography fontWeight={700}>{data.name}</Typography>
      <Typography variant="caption" color="text.secondary">
        {data.criterionLabel}
      </Typography>
      <Typography variant="body2" mt={0.5}>
        {data.emailCount} email{data.emailCount === 1 ? "" : "s"}
      </Typography>
      {data.activeCount !== undefined && (
        <Chip
          size="small"
          color="secondary"
          sx={{ mt: 1 }}
          label={`${data.activeCount} active`}
        />
      )}
      {!data.isTerminal && (
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={!data.readOnly}
          style={data.readOnly ? { background: "#00897b" } : undefined}
        />
      )}
    </Box>
  );
}

const nodeTypes = { stage: StageNode };
export default function WorkflowCanvas({
  stages,
  transitions,
  criteria,
  selectedKey,
  dispatch,
  readOnly = false,
  stageMetrics = {},
}) {
  const criterionMap = useMemo(
    () => Object.fromEntries(criteria.map((c) => [c.code, c])),
    [criteria],
  );
  const nodes = useMemo(
    () =>
      stages.map((stage) => ({
        id: stage.key,
        type: "stage",
        position: {
          x: stage.canvasX ?? 80,
          y: stage.canvasY ?? stage.order * 140,
        },
        selected: selectedKey === stage.key,
        data: {
          ...stage,
          criterionLabel:
            criterionMap[stage.criterionCode]?.label || stage.criterionCode,
          emailCount: stage.templates.filter((e) => e.isEnabled).length,
          activeCount: stageMetrics[stage.id]?.active,
          readOnly,
        },
      })),
    [stages, selectedKey, criterionMap, readOnly, stageMetrics],
  );
  const edges = useMemo(
    () =>
      transitions.map((edge) => {
        const source = edge.sourceKey || edge.sourceStage?.key;
        const target = edge.targetKey || edge.targetStage?.key;
        return {
          id: edge.id || `${source}-${target}`,
          source,
          target,
          label: edge.outcome,
          animated: true,
          style: readOnly ? { stroke: "#00897b", strokeWidth: 2.5 } : undefined,
        };
      }),
    [transitions, readOnly],
  );
  const onConnect = useCallback(
    ({ source, target }) => {
      if (readOnly) return;
      const stage = stages.find((s) => s.key === source);
      if (!source || !target || source === target || stage?.isTerminal) return;
      dispatch({
        type: "connect",
        sourceKey: source,
        targetKey: target,
        outcome:
          criterionMap[stage.criterionCode]?.transitionOutcome ||
          "criterion_met",
      });
    },
    [stages, criterionMap, dispatch, readOnly],
  );
  return (
    <Box
      sx={{
        height: 520,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onConnect={onConnect}
        onNodeClick={(_, node) => dispatch({ type: "select", key: node.id })}
        onNodeDragStop={(_, node) =>
          dispatch({
            type: "position",
            key: node.id,
            x: node.position.x,
            y: node.position.y,
          })
        }
        fitView
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable
        deleteKeyCode={null}
      >
        <Background />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </Box>
  );
}
